import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';

import { listDrafts } from '../../../api/paymentsApi/settlementsApi';
import Loader from '../../../components/Loader';
import NoDataText from '../../../components/NoDataText';
import TableContainer from '../../../components/Table/TableContainer';
import { MemoDraftSummary, ActiveSortField, FilterTypes } from '../../../types';
import useSnackBar from '../../../hooks/useSnackBar';
import DraftsTable from '../../PaymentsView/DraftsTab/DraftsTable';
import TableActions from '../../../components/Table/TableActions';
import TableFilters from '../../../components/TableFilters/TableFilters';
import usePagination from '../../../hooks/usePagination';

// Table column labels for case-specific drafts (no case info needed)
const getCaseDraftsListTableLabels = () => [
  {
    accessor: '',
    isSortable: false,
    label: 'Client',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Defendant',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Settled Amount',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Filename',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Updated',
  },
  {
    accessor: '',
    isSortable: false,
    label: 'Created',
  },
];

const CaseDraftsTab = () => {
  const { caseId } = useParams();
  const { setAlert } = useSnackBar();
  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();

  const [draftsList, setDraftsList] = useState<MemoDraftSummary[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [filters, setFilters] = useState<FilterTypes>({
    defendantId: '',
  });

  useEffect(() => {
    if (!caseId) return;

    setIsDataLoading(true);
    // Merge caseId from route params with other filters
    const combinedFilters = { ...filters, caseId: Number(caseId) };
    listDrafts(pageNumber, rowsPerPage, combinedFilters)
      .then((res) => {
        if (res.data) {
          setDraftsList(res.data.data);
          pageNumber === 1 && setTotalCount?.(res.data.total || 0);
        }
      })
      .catch((error) => {
        setAlert({
          message: error.response?.data?.message || 'Failed to load drafts',
          type: 'error',
        });
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [caseId, pageNumber, rowsPerPage, filters, setAlert, setTotalCount]);

  const tableLabels = getCaseDraftsListTableLabels();

  // const exportList = () => {
  //   setAlert({
  //     message: 'Draft export functionality will be available soon.',
  //     type: 'info',
  //   });
  // };

  return (
    <>
      <TableActions
        hasParams={true}
        // exportList={exportList}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
      />

      {showFilters && (
        <TableFilters
          filters={filters}
          setFilters={setFilters}
          defendant
          timeFilter="Updated Date"
        />
      )}

      {isDataLoading && !draftsList.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : !draftsList.length ? (
        <NoDataText />
      ) : (
        <TableContainer
          headerLabels={tableLabels}
          activeSortField={activeSortField}
          setActiveSortField={setActiveSortField}
        >
          <DraftsTable tableData={draftsList} showCaseInfo={false} />
        </TableContainer>
      )}
    </>
  );
};

export default CaseDraftsTab;
